import _ from "lodash"
import crypto from "crypto"

import { APP_VERSION } from "../cloud-config"
import { Profile, User, UserProfileSchema } from "../models"
import { linkDocsToProfile, uploadAdHocDataRecord } from "../providers/endpointDataProvider/dataSync"
import { getRemoteFile, FilePathProvider, fetchProfileData } from "../providers"
import { processTypes, replaceNullOrUndefined, toLowerCaseCustom } from "../utils"
import { SessionService } from "./sessionService"
import {
  AssociationId,
  AuthCredentials,
  CohortName,
  EmploymentStatus,
  FieldConfigKey,
  FileConfig,
  ProfilePk,
  ProfileSyncSessionId,
  TrashStatus,
} from "../types"
import { AssociationSettingsRepository, ProfileRepository } from "../repositories"
import { compulsoryRecordFieldKeys } from "../config"
import { getNextBatchFileParams, markBatchCompletion, prepareProfileDownload } from "../handlers/api/profileHandlers"
import AdHocRecord from "../models/adHocRecord/adHocRecord"

export class ProfileService {
  async assignDocumentsToProfile(
    profilePk: ProfilePk,
    association: string,
    newlyCreatedDocs: any,
    userProfile: UserProfileSchema,
    authCredentials: AuthCredentials,
  ): Promise<any> {
    const params = {
      profilePk,
      newlyCreatedDocs,
      userInfo: userProfile,
    }
    try {
      const { username, password } = authCredentials
      const token = await SessionService.prepareAuthTokens(username, password)
      const data = await linkDocsToProfile(association, params, token)
      const { importTracker } = data
      return importTracker
    } catch (error) {
      throw error
    }
  }

  async saveProfileChanges(
    segmentData: {
      association: AssociationId
      selectedCohort: CohortName
      selectedEmploymentStatus: EmploymentStatus
    },
    newData: Record<string, any>,
    rawDataChanges: Record<string, { old: any; new: any }>,
    profileEntity: Profile,
    userEntity: User,
    profileRepo: ProfileRepository,
    authCredentials: AuthCredentials,
  ): Promise<any> {
    try {
      const convertToRawData = (data: Record<string, { old: any; new: any }>) => {
        return Object.keys(data).reduce((acc: Record<string, any>, key) => {
          acc[key] = data[key].old
          return acc
        }, {})
      }

      const { association, selectedCohort, selectedEmploymentStatus } = segmentData
      profileEntity.updateGeneralData(newData)

      const profilePk = profileEntity.getPk()
      const compulsoryRecordData = this.extractCompulsoryRecordData(
        profileEntity,
        userEntity,
        association,
        selectedEmploymentStatus,
        selectedCohort,
      )
      const processRecord = new AdHocRecord({
        rawData: { ...newData, ...compulsoryRecordData },
        previousRawData: convertToRawData(rawDataChanges),
      })
      processRecord.addTimestamp()
      const { username, password } = authCredentials
      const token = await SessionService.prepareAuthTokens(username, password)

      await uploadAdHocDataRecord(association, profilePk, [processRecord.convertToJson()], token)
      profileEntity.addAdHocDataRecord(association, processRecord.convertToJson())
      await profileRepo.updateProfiles({ [profilePk]: profileEntity })
    } catch (error) {
      throw error
    }
  }

  private extractCompulsoryRecordData(
    // TODO: Ensure app adds process, association, cohort
    profileEntity: Profile,
    userEntity: User,
    association: AssociationId,
    employmentStatus: EmploymentStatus,
    selectedCohort: CohortName,
  ) {
    const process =
      employmentStatus === EmploymentStatus.CANDIDATE ? processTypes.generalCandidate : processTypes.generalEmployee

    let compulsoryData = {
      profilePk: profileEntity.getPk(),
      appVersion: `WEB - ${APP_VERSION}`,
      deviceUUID: "",
      process,
      association,
      cohort: selectedCohort,
      division: selectedCohort,
    } as Record<string, any>

    if (userEntity) {
      const { username, name, surname } = userEntity.getPersonalUserInfo()
      compulsoryData = {
        ...compulsoryData,
        username,
        userFirstName: name,
        userSurname: surname,
      }
    }
    compulsoryRecordFieldKeys.forEach((key) => (compulsoryData[key] = profileEntity.getGeneralDataValue(key)))
    return compulsoryData
  }

  checkChangesForUniqueFieldViolation(
    profileRepo: ProfileRepository,
    associationRepo: AssociationSettingsRepository,
    association: string,
    fieldChanges: Record<string, string | string[]>,
  ) {
    const uniqueFields = associationRepo.getUniqueFieldKeysFromConfig(association)
    if (!uniqueFields.length) {
      return { exists: false, location: "" }
    }
    let exists = false
    let location = ""
    let matchingFieldLabel = ""
    const profileEntities = profileRepo.getAllProfileEntities()

    for (const profileEntity of Object.values(profileEntities)) {
      const matchingFieldValueFound = uniqueFields.find((fieldKey: FieldConfigKey) => {
        const existingFieldValue = profileEntity.getGeneralDataValue(fieldKey)
        if (!fieldChanges.hasOwnProperty(fieldKey)) {
          return false
        }
        const newFieldValue = fieldChanges[fieldKey]
        const match = !!newFieldValue && toLowerCaseCustom(existingFieldValue) === toLowerCaseCustom(newFieldValue)
        if (match) {
          matchingFieldLabel = associationRepo.getFieldLabelFromConfig(association, fieldKey)
          return true
        }
        return false
      })
      if (matchingFieldValueFound) {
        const employmentStatus = profileEntity.getEmploymentStatus()
        const trashStatus = profileEntity.getTrashStatus()
        exists = true
        location = trashStatus === TrashStatus.TRASHED ? TrashStatus.TRASHED : employmentStatus
        break
      }
    }
    return { exists, location, fieldLabel: matchingFieldLabel }
  }

  async downloadAssociationProfiles(
    isFreshDownload: boolean,
    sessionService: SessionService,
    associationRepo: AssociationSettingsRepository,
    profileRepo: ProfileRepository,
    authCredentials: AuthCredentials,
    targetAssociation: AssociationId,
    targetCohort?: CohortName,
    setUiProgress?: (progress: string) => void,
  ) {
    const newAssociationSettingsJson = await sessionService.hydrateAssociationSettings(
      [targetAssociation],
      authCredentials,
    )
    await associationRepo.initialise(newAssociationSettingsJson)
    const syncId = `${new Date().getTime()}`

    const targetProfilePks = associationRepo.getAuthorisedPks(targetAssociation, targetCohort)
    await this.prepareProfileDownload(
      isFreshDownload,
      targetAssociation,
      syncId,
      authCredentials,
      profileRepo,
      targetProfilePks,
    )
    await this.wait(3000) // Under the hood, prepareProfileDownload doesn't await response from the API, as it's a long-running process. We need to wait for the initial sync marker to be written before downloadProfileBatches kicks off.
    await this.downloadProfileBatches(
      profileRepo,
      targetAssociation,
      syncId,
      authCredentials,
      targetProfilePks.length,
      setUiProgress,
    )
    const targetProfilePksAllCohorts = associationRepo.getAuthorisedPks(targetAssociation)
    await profileRepo.removeUnAuthedProfiles(targetProfilePksAllCohorts)
    await profileRepo.setProfilesDownloadedMs(new Date().getTime(), targetCohort)
  }

  async downloadProfiles(
    association: AssociationId,
    profileRepo: ProfileRepository,
    authCredentials: AuthCredentials,
    targetPks: ProfilePk[],
  ) {
    const syncId = `${new Date().getTime()}`
    await this.prepareProfileDownload(true, association, syncId, authCredentials, profileRepo, targetPks)
    await this.downloadProfileBatches(profileRepo, association, syncId, authCredentials, 1)
  }

  async refreshProfile(
    association: AssociationId,
    profileRepo: ProfileRepository,
    authCredentials: AuthCredentials,
    targetPk: ProfilePk,
  ) {
    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    const profiles = await fetchProfileData(association, [targetPk], token)
    await profileRepo.updateProfiles(profiles)
  }

  prepareProfileDownload = async (
    isFreshDownload: boolean,
    association: AssociationId,
    syncId: ProfileSyncSessionId,
    authCredentials: AuthCredentials,
    profileRepo: ProfileRepository,
    targetProfilePks: ProfilePk[],
  ) => {
    const packageEmptyHashes = (targetProfilePks: ProfilePk[]) => {
      return targetProfilePks.reduce((acc: any, profilePk: ProfilePk) => {
        acc[profilePk] = ""
        return acc
      }, {})
    }
    try {
      const { username, password } = authCredentials
      const token = await SessionService.prepareAuthTokens(username, password)
      let profileHashes = {}
      if (isFreshDownload) {
        profileHashes = packageEmptyHashes(targetProfilePks)
      } else {
        profileHashes = this.createProfileHashes(profileRepo, targetProfilePks)
      }
      prepareProfileDownload(association, syncId, username, profileHashes, token)
    } catch (error) {
      console.log("error: ", error)
      throw error
    }
  }

  async downloadProfileBatches(
    profileRepo: ProfileRepository,
    association: AssociationId,
    currentSessionId: string,
    authCredentials: AuthCredentials,
    totalNumberOfProfiles: number,
    setUiProgress = (progress: string) => {},
  ) {
    const buildUpdateMessage = (progress: number, total: number) => {
      return `${progress} new profile update${progress === 1 ? "" : "s"}...`
    }

    const finishProgressUiLoader = (
      currentProgress: number,
      targetProgress: number,
      uiUpdateHandler = (progress: string) => {},
    ) => {
      for (currentProgress; currentProgress < targetProgress; currentProgress++) {
        const updateMessage = buildUpdateMessage(currentProgress, targetProgress)
        uiUpdateHandler(updateMessage)
      }
    }

    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    let isMoreToDownload = true
    let profilesDownloadedCount = 0
    do {
      try {
        const { isBatch, isLoading, fileConfig } = await getNextBatchFileParams(
          association,
          currentSessionId,
          username,
          token,
        )
        if (isLoading) {
          await this.wait(3000)
          continue
        }
        if (isBatch) {
          const { fileName, fileFormat } = fileConfig
          const profiles = (await this.downloadProfileBatch(profileRepo, fileConfig)) || {}
          await markBatchCompletion(association, currentSessionId, username, `${fileName}.${fileFormat}`, token)
          profilesDownloadedCount += Object.keys(profiles).length
          const updateMessage = buildUpdateMessage(profilesDownloadedCount, totalNumberOfProfiles)
          setUiProgress(updateMessage)
        } else {
          isMoreToDownload = false
        }
      } catch (error) {
        console.log("error: ", error)
      }
    } while (isMoreToDownload)
    finishProgressUiLoader(profilesDownloadedCount, totalNumberOfProfiles)
  }

  downloadProfileBatch = async (profileRepo: ProfileRepository, fileConfig: FileConfig) => {
    //@ts-ignore
    const { fileDir, fileName, fileFormat } = fileConfig
    const remotePath = `${fileDir}/${fileName}.${fileFormat}`
    const maybeProfiles = await getRemoteFile(remotePath, { download: true, cacheControl: "no-cache", isJson: true })
    if (!maybeProfiles) {
      return
    }
    await profileRepo.updateProfiles(maybeProfiles)
    return maybeProfiles
  }

  createProfileHashes = (profileRepo: ProfileRepository, profilePks: ProfilePk[]) => {
    try {
      const profileHashes = {} as Record<ProfilePk, string>
      let errors = []
      profilePks.forEach((profilePk) => {
        try {
          profileHashes[profilePk] = ""
          const profileEntity = profileRepo.getProfileEntity(profilePk)
          if (!profileEntity) {
            return
          }
          const simplifiedProfile = replaceNullOrUndefined({
            uid: profileEntity.getUid(),
            idPassport: profilePk,
            docs: profileEntity.getAllDocs(),
            cohorts: profileEntity.getCohorts(),
            trashStatus: profileEntity.getTrashStatus(),
            generalData: profileEntity.getGeneralData(),
            updatedMs: profileEntity.getUpdatedMs(),
          })

          const profileString = JSON.stringify(simplifiedProfile)
          const PROBLEM_CHARS = ['"', ",", ":", "[", "]", "{", "}"]
          const sortedString = profileString // This is done to ensure consistent calculation of hash on frontend and backend in case JSON is ordered differently
            .split("")
            .sort()
            .filter((char) => !PROBLEM_CHARS.includes(char))
            .join("")
          const hash = crypto.createHash("md5").update(sortedString).digest("hex")
          profileHashes[profilePk] = hash
        } catch (error) {
          errors.push({ pk: profilePk, error })
        }
      })
      return profileHashes
    } catch (error) {
      console.log("createProfileHashes: ", error)
      throw error
    }
  }

  async getProfilePic(profilePk: ProfilePk) {
    const remoteUrl = FilePathProvider.getRemoteProfilePicFilePath(profilePk)
    return await getRemoteFile(remoteUrl)
  }

  private wait = async (msToPause: number) => {
    return new Promise((resolve) => setTimeout(resolve, msToPause))
  }
}
